import { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { ShieldCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";

const E = (e) => formatApiErrorDetail(e.response?.data?.detail) || "Erreur";

/** Écran unique des droits d'un salarié : Opérations (contrôles, pointage, commandes, scanner) + Fidélité & bons cadeaux. */
export function EmployeePermissions({ estId, employee, reload }) {
  const [open, setOpen] = useState(false);
  const [d, setD] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = () => api.get(`/staffing/${estId}/employees/${employee.id}/permissions`).then((r) => setD(r.data)).catch((e) => toast.error(E(e)));
  useEffect(() => { if (open) load(); }, [open]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!["active", "invited", "opened"].includes(employee.access.status)) return null;

  const toggle = async (gk, k) => {
    const g = d.groups.find((x) => x.key === gk);
    const value = !(g.values[k] === true);
    setBusy(true);
    try { const { data } = await api.put(`/staffing/${estId}/employees/${employee.id}/permissions`, { [gk]: { [k]: value } }); setD(data); reload?.(); }
    catch (e) { toast.error(E(e)); load(); } finally { setBusy(false); }
  };
  const setAll = async (gk, v) => {
    const g = d.groups.find((x) => x.key === gk);
    setBusy(true);
    try { const { data } = await api.put(`/staffing/${estId}/employees/${employee.id}/permissions`, { [gk]: Object.fromEntries(Object.keys(g.labels).map((k) => [k, v])) }); setD(data); reload?.(); toast.success(v ? "Tous les droits accordés" : "Tous les droits retirés"); }
    catch (e) { toast.error(E(e)); load(); } finally { setBusy(false); }
  };

  return <>
    <Button size="sm" variant="outline" className="rounded-lg h-8 text-xs" onClick={() => setOpen(true)} data-testid={`perms-open-${employee.id}`}><ShieldCheck className="w-3.5 h-3.5 mr-1" />Droits</Button>
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetContent className="overflow-y-auto w-full sm:max-w-md" data-testid="perms-sheet">
        <SheetHeader><SheetTitle>Droits de {employee.first_name} {employee.last_name}</SheetTitle></SheetHeader>
        <p className="text-xs text-slate-500 mt-1">Rôle : {employee.role || "—"}. Les droits sont individuels : un salarié autorisé n'est jamais bloqué par le nom de son rôle.</p>
        {!d ? <p className="text-sm text-slate-500 mt-4">Chargement…</p> : d.groups.map((g) => (
          <section key={g.key} className="mt-5" data-testid={`perms-group-${g.key}`}>
            <div className="flex items-center justify-between mb-2"><h3 className="font-semibold text-sm">{g.label}</h3>
              <span className="text-[11px] space-x-2"><button className="underline text-slate-500" disabled={busy} onClick={() => setAll(g.key, true)} data-testid={`perms-all-${g.key}`}>Tout</button><button className="underline text-slate-500" disabled={busy} onClick={() => setAll(g.key, false)} data-testid={`perms-none-${g.key}`}>Aucun</button></span></div>
            <ul className="divide-y divide-slate-100 rounded-xl border border-slate-200">
              {Object.entries(g.labels).map(([k, l]) => <li key={k} className="flex items-center justify-between gap-3 px-3 py-2 text-sm">
                <span>{l}</span>
                <input type="checkbox" className="w-4 h-4 accent-slate-900" checked={!!g.values[k]} disabled={busy} onChange={() => toggle(g.key, k)} data-testid={`perm-${g.key}-${k}`} />
              </li>)}
            </ul>
          </section>))}
      </SheetContent>
    </Sheet>
  </>;
}
